import React from "react";
import { useTranslation } from "react-i18next";
import { SonicUser } from "../../../common/classes/SonicUser";
import styles from "../db-list/detail/DatabaseDetail.module.scss";
import { timestampToTime } from "../../../utils/GeneralUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SonicLink from "../../../common/routing/SonicLink";

type Props = {
  user: SonicUser;
  database_entries: Array<{ id: string; name: string; status: string }>;
  teams: Array<{ id: string; name: string; status: string }>;
  projects: Array<{ id: string; name: string; status: string }>;
  events: Array<{ id: string; name: string; status: string }>;
  topics: Array<{ id: string; name: string; status: string }>;
  facilities: Array<{ id: string; name: string; status: string }>;
  isSelect: boolean;
  pageTitle: string;
  handleUpdateDenial: (denial: boolean) => void;
};

const UserOverview: React.FC<Props> = ({
  user,
  database_entries,
  teams,
  projects,
  events,
  topics,
  facilities,
  isSelect,
  pageTitle,
  handleUpdateDenial,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <h1 className={styles["f-databaseDetail__title"]}>{pageTitle}</h1>
      {isSelect && (
        <div className={styles["f-databaseDetail__status"]}>
          <span>チャット状況： {user.meta.chat_denial ? "拒否" : "許可"}</span>
          <button
            className={`${styles["f-databaseDetail__status-btn"]} ${
              user.meta.chat_denial ? styles["approval"] : styles["denial"]
            }`}
            onClick={() => handleUpdateDenial(!user.meta.chat_denial)}
          >
            {user.meta.chat_denial ? "許可に変更" : "拒否に変更"}
          </button>
        </div>
      )}
      <div className={styles["f-databaseDetail__wrap"]}>
        <div className={styles["f-databaseDetail__info"]}>
          <span className={styles["f-databaseDetail__info-title"]}>
            ユーザー名
          </span>
          <span className={styles["f-databaseDetail__info-content"]}>
            {user.meta.name}
          </span>
        </div>
        <div className={styles["f-databaseDetail__info"]}>
          <span className={styles["f-databaseDetail__info-title"]}>氏名</span>
          <span className={styles["f-databaseDetail__info-content"]}>
            {user.meta.full_name ?? "ー"}
          </span>
        </div>
        <div className={styles["f-databaseDetail__info"]}>
          <span className={styles["f-databaseDetail__info-title"]}>
            メールアドレス
          </span>
          <span className={styles["f-databaseDetail__info-content"]}>
            {user.meta.email}
            <FontAwesomeIcon
              icon={["fas", "user-check"]}
              className={
                user.meta.active === 1 ? styles["active"] : styles["inactive"]
              }
            />
          </span>
        </div>
        <div className={styles["f-databaseDetail__info"]}>
          <span className={styles["f-databaseDetail__info-title"]}>
            生年月日
          </span>
          <span className={styles["f-databaseDetail__info-content"]}>
            {user.meta.birthday
              ? timestampToTime(user.meta.birthday, false)
              : "ー"}
          </span>
        </div>
        <div className={styles["f-databaseDetail__info"]}>
          <span className={styles["f-databaseDetail__info-title"]}>
            電話番号
          </span>
          <span className={styles["f-databaseDetail__info-content"]}>
            {user.meta.phone ?? "ー"}
          </span>
        </div>
        <div className={styles["f-databaseDetail__info"]}>
          <span className={styles["f-databaseDetail__info-title"]}>
            都道府県
          </span>
          <span className={styles["f-databaseDetail__info-content"]}>
            {user.meta.prefecture ?? "ー"}
          </span>
        </div>
        <div className={styles["f-databaseDetail__info"]}>
          <span className={styles["f-databaseDetail__info-title"]}>住所</span>
          <span className={styles["f-databaseDetail__info-content"]}>
            {user.meta.address_line_1 ?? "ー"}
            {user.meta.address_line_2}
          </span>
        </div>
        {!isSelect ? (
          <>
            <div className={styles["f-databaseDetail__info"]}>
              <span className={styles["f-databaseDetail__info-title"]}>
                学校
              </span>
              <span className={styles["f-databaseDetail__info-content"]}>
                {user.meta.school?.name ?? "ー"}
              </span>
            </div>
            <div className={styles["f-databaseDetail__info"]}>
              <span className={styles["f-databaseDetail__info-title"]}>
                コース
              </span>
              <span className={styles["f-databaseDetail__info-content"]}>
                {user.meta.course?.name ?? "ー"}
              </span>
            </div>
          </>
        ) : null}

        <div className={styles["f-databaseDetail__info"]}>
          <div className={styles["f-databaseDetail__info-title"]}>
            {t("プロフィール")}
            <div className={styles["f-databaseDetail__info-title__remarks"]}>
              公開中のみ
            </div>
          </div>
          <div
            className={`${styles["f-databaseDetail__info-content"]} ${styles["vertical"]}`}
          >
            {database_entries && database_entries.length
              ? database_entries.map((database_entry, i) => (
                  <div key={i}>
                    <SonicLink
                      to={`/person/${database_entry.id}`}
                      newTab={true}
                    >
                      {database_entry.name}
                      <FontAwesomeIcon icon={["fal", "external-link-alt"]} />
                    </SonicLink>
                  </div>
                ))
              : "ー"}
          </div>
        </div>

        <div className={styles["f-databaseDetail__info"]}>
          <div className={styles["f-databaseDetail__info-title"]}>
            {t("チーム")}
            <div className={styles["f-databaseDetail__info-title__remarks"]}>
              公開中のみ
            </div>
          </div>
          <div
            className={`${styles["f-databaseDetail__info-content"]} ${styles["vertical"]}`}
          >
            {teams && teams.length
              ? teams.map((team, i) => (
                  <div key={i}>
                    <SonicLink to={`/team/${team.id}`} newTab={true}>
                      {team.name}
                      <FontAwesomeIcon icon={["fal", "external-link-alt"]} />
                    </SonicLink>
                  </div>
                ))
              : "ー"}
          </div>
        </div>

        <div className={styles["f-databaseDetail__info"]}>
          <div className={styles["f-databaseDetail__info-title"]}>
            {t("プロジェクト")}
            <div className={styles["f-databaseDetail__info-title__remarks"]}>
              公開中のみ
            </div>
          </div>
          <div
            className={`${styles["f-databaseDetail__info-content"]} ${styles["vertical"]}`}
          >
            {projects && projects.length
              ? projects.map((project, i) => (
                  <div key={i}>
                    <SonicLink to={`/project/${project.id}`} newTab={true}>
                      {project.name}
                      <FontAwesomeIcon icon={["fal", "external-link-alt"]} />
                    </SonicLink>
                  </div>
                ))
              : "ー"}
          </div>
        </div>

        <div className={styles["f-databaseDetail__info"]}>
          <div className={styles["f-databaseDetail__info-title"]}>
            {t("イベント")}
            <div className={styles["f-databaseDetail__info-title__remarks"]}>
              公開中のみ
            </div>
          </div>
          <div
            className={`${styles["f-databaseDetail__info-content"]} ${styles["vertical"]}`}
          >
            {events && events.length
              ? events.map((event, i) => (
                  <div key={i}>
                    <SonicLink to={`/event/${event.id}`} newTab={true}>
                      {event.name}
                      <FontAwesomeIcon icon={["fal", "external-link-alt"]} />
                    </SonicLink>
                  </div>
                ))
              : "ー"}
          </div>
        </div>

        {isSelect ? (
          <div className={styles["f-databaseDetail__info"]}>
            <div className={styles["f-databaseDetail__info-title"]}>
              {t("トピックス")}
              <div className={styles["f-databaseDetail__info-title__remarks"]}>
                公開中のみ
              </div>
            </div>
            <div
              className={`${styles["f-databaseDetail__info-content"]} ${styles["vertical"]}`}
            >
              {topics && topics.length
                ? topics.map((topic, i) => (
                    <div key={i}>
                      <SonicLink to={`/topic/${topic.id}`} newTab={true}>
                        {topic.name}
                        <FontAwesomeIcon icon={["fal", "external-link-alt"]} />
                      </SonicLink>
                    </div>
                  ))
                : "ー"}
            </div>
          </div>
        ) : null}

        {isSelect ? (
          <div className={styles["f-databaseDetail__info"]}>
            <div className={styles["f-databaseDetail__info-title"]}>
              {t("eスポーツ施設")}
              <div className={styles["f-databaseDetail__info-title__remarks"]}>
                公開中のみ
              </div>
            </div>
            <div
              className={`${styles["f-databaseDetail__info-content"]} ${styles["vertical"]}`}
            >
              {facilities && facilities.length
                ? facilities.map((facility, i) => (
                    <div key={i}>
                      <SonicLink to={`/facility/${facility.id}`} newTab={true}>
                        {facility.name}
                        <FontAwesomeIcon icon={["fal", "external-link-alt"]} />
                      </SonicLink>
                    </div>
                  ))
                : "ー"}
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
};

export default UserOverview;
